import ThrottledApi from '../api/throttledApi';
import { AxiosResponse } from 'axios';
import { PRICEBOOK_ENDPOINTS } from '../../constants/apiEndPoints';
import { InventorySummaryModel, ItemInventorySettingsModel, ItemInventoryMovementsModel, ItemPurchasePricingModel, GasPriceModel, SalesSummaryModel, MonthlySalesSummaryModel, PriceComparisonModel, PricebookMetadataModel, PurchaseVendorsListModel, BulkUpdateItemsListModel, LinkedCodeListModel } from '../../models';
// Import shared types from pricebook/types
// Import items-specific types from items/types
import {
  ApiPricebookItem,
  PricebookItemsApiResponse,
  CreatePricebookItemRequest,
  CreatePricebookItemResponse,
  UpdatePricebookItemRequest,
  UpdatePricebookItemResponse,
  UpdateInventorySettingsRequest,
  UpdateInventorySettingsResponse,
  UpdateItemOpeningBalanceRequest,
  UpdateItemOpeningBalanceResponse,
  CreateItemInventoryAdjustmentRequest,
  CreateItemInventoryAdjustmentResponse,
  CreateManualPurchaseLineRequest,
  CreateManualPurchaseLineResponse,
  CreateStoreVendorRequest,
  CreateStoreVendorResponse,
  DeletePricebookItemResponse,
  ExpireTransitionPricebookItemResponse,
  SendItemsToPosRequest,
  SendItemsToPosResponse,
  BulkUpdatePricebookItemsRequest,
  BulkUpdatePricebookItemsResponse,
  ItemHistoryResponse,
  BulkUpdateItemsApiResponse,
  BulkMultiStoreItemsFilters,
} from '../../features/pricebook/types/items';
import {
  CreateLinkedCodeRequest,
  CreateLinkedCodeResponse,
  UpdateLinkedCodeRequest,
  UpdateLinkedCodeResponse,
} from '../../features/pricebook/types/linkedCode';
import {
  UpdateGasPriceRequest,
  UpdateGasPriceResponse,
} from '../../features/pricebook/types/gasprice';
import PriceGroupsListModel from '../../models/pricebook/PriceGroupsModel';
import { PriceGroupDetailModel } from '../../models/pricebook/PriceGroupDetailModel';

/**
 * PricebookService
 * 
 * This service handles fetching pricebook data using ThrottledApi
 * for automatic request queuing and rate limiting.
 * 
 * Note: Types are defined in the feature folder (features/pricebook/items/types)
 * to keep all pricebook-related types co-located with the feature.
 */

/**
 * Response type for getStorePricebookItems - returns raw paginated API data
 */
export interface PricebookItemsServiceResponse {
  success: boolean;
  data: PricebookItemsApiResponse | null;
  error: any;
}

export interface PricebookItemDetailServiceResponse {
  success: boolean;
  data: ApiPricebookItem | null;
  error: any;
}

export interface BulkMultiStoreItemsServiceResponse {
  success: boolean;
  data: BulkUpdateItemsListModel | null;
  error: any;
}

/**
 * Response type for getInventorySummary - returns InventorySummaryModel
 */
export interface InventorySummaryServiceResponse {
  success: boolean;
  data: InventorySummaryModel | null;
  error: any;
}

export interface ItemInventorySettingsServiceResponse {
  success: boolean;
  data: ItemInventorySettingsModel | null;
  error: any;
}

export interface ItemInventoryMovementsServiceResponse {
  success: boolean;
  data: ItemInventoryMovementsModel | null;
  error: any;
}

export interface FinalizedPurchaseVendorsServiceResponse {
  success: boolean;
  data: PurchaseVendorsListModel | null;
  error: any;
}

export interface LinkedCodesServiceResponse {
  success: boolean;
  data: LinkedCodeListModel | null;
  error: any;
}

export interface PriceGroupsServiceResponse {
  success: boolean;
  data: PriceGroupsListModel | null;
  error: any;
}

export interface PriceGroupDetailServiceResponse {
  success: boolean;
  data: PriceGroupDetailModel | null;
  error: any;
}

export interface AssignPriceGroupItemsRequest {
  item_ids: string[];
}

export interface AssignPriceGroupItemsServiceResponse {
  success: boolean;
  data: any;
  error: any;
}

export interface ItemPurchasePricingServiceResponse {
  success: boolean;
  data: ItemPurchasePricingModel | null;
  error: any;
}

/**
 * Get store pricebook items
 * 
 * @param storeId - The ID of the store
 * @param filters - Optional filters (department_code, tax_code, status)
 * @param page - Page number (default: 1)
 * @param pageSize - Number of items per page (default: 50)
 * @param search - Optional search term for filtering items
 * @returns Promise<PricebookItemsServiceResponse> with paginated raw API data
 */
export const getStorePricebookItems = async (
  storeId: string,
  filters?: {
    department_code?: string;
    tax_code?: string;
    pricegroup?: string;
    status?: string;
    food_stampable?: boolean;
    age_restriction?: string;
    linked_code?: string;
    vendor?: string;
    start_date?: string;
    end_date?: string;
  },
  page: number = 1,
  pageSize: number = 50,
  search?: string
): Promise<PricebookItemsServiceResponse> => {
  try {
    let url = PRICEBOOK_ENDPOINTS.GET_STORE_ITEMS(storeId);
    
    // Build query parameters
    const queryParams: string[] = [];
    
    // Add pagination parameters
    queryParams.push(`page=${page}`);
    queryParams.push(`page_size=${pageSize}`);
    
    // Add filter parameters (always include all filters, they will have "all" as default)
    if (filters?.department_code) {
      queryParams.push(`department_code=${encodeURIComponent(filters.department_code)}`);
    }
    if (filters?.tax_code) {
      queryParams.push(`tax_code=${encodeURIComponent(filters.tax_code)}`);
    }
    if (filters?.pricegroup) {
      queryParams.push(`pricegroup=${encodeURIComponent(filters.pricegroup)}`);
    }
    if (filters?.status) {
      queryParams.push(`status=${encodeURIComponent(filters.status)}`);
    }
    if (filters?.food_stampable !== undefined) {
      queryParams.push(`food_stampable=${encodeURIComponent(String(filters.food_stampable))}`);
    }
    if (filters?.age_restriction) {
      queryParams.push(`age_restriction=${encodeURIComponent(filters.age_restriction)}`);
    }
    if (filters?.linked_code) {
      queryParams.push(`linked_code=${encodeURIComponent(filters.linked_code)}`);
    }
    if (filters?.vendor) {
      queryParams.push(`vendor=${encodeURIComponent(filters.vendor)}`);
    }
    if (filters?.start_date) {
      queryParams.push(`start_date=${encodeURIComponent(filters.start_date)}`);
    }
    if (filters?.end_date) {
      queryParams.push(`end_date=${encodeURIComponent(filters.end_date)}`);
    }
    
    // Add search parameter if provided
    if (search && search.trim() !== '') {
      queryParams.push(`search=${encodeURIComponent(search.trim())}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    const response = await ThrottledApi.get(url, {}) as AxiosResponse;
    const responseData = response?.data;

    // Handle error response with detail key
    if (responseData?.detail) {
      return {
        success: false,
        data: null,
        error: responseData.detail,
      };
    }

    // Handle success response: { page, page_size, total_pages, has_next, has_previous, total_count, items: [...] }
    // or empty response: { page, page_size, total_pages: 0, has_next: false, has_previous: false, total_count: 0, items: [] }
    if (responseData && (responseData.items || Array.isArray(responseData))) {
      const dataToProcess: PricebookItemsApiResponse = Array.isArray(responseData)
        ? {
          page,
          page_size: pageSize,
          total_pages: 1,
          has_next: false,
          has_previous: false,
          total_count: responseData.length,
          items: responseData,
        }
        : responseData;

      return {
        success: true,
        data: dataToProcess,
        error: null,
      };
    }

    // Handle empty or unexpected response
    return {
      success: true,
      data: {
        page,
        page_size: pageSize,
        total_pages: 0,
        has_next: false,
        has_previous: false,
        total_count: 0,
        items: [],
      },
      error: null,
    };
  } catch (error: any) {
    // Handle error response with detail key
    const errorData = error.response?.data;
    const errorMessage = errorData?.detail || errorData || error.message || 'Failed to fetch pricebook items';
    
    return {
      success: false,
      data: null,
      error: errorMessage,
    };
  }
};

/**
 * Get one store pricebook item detail (includes variants/packs when available)
 */
export const getStorePricebookItemDetail = async (
  storeId: string,
  scanCode: string
): Promise<PricebookItemDetailServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_STORE_ITEM_DETAIL(storeId, scanCode),
      {}
    ) as AxiosResponse;

    const responseData = response?.data;
    if (responseData?.detail) {
      return { success: false, data: null, error: responseData.detail };
    }
    if (responseData) {
      return { success: true, data: responseData as ApiPricebookItem, error: null };
    }
    return { success: false, data: null, error: 'No data received' };
  } catch (error: any) {
    const errorData = error.response?.data;
    const errorMessage = errorData?.detail || errorData || error.message || 'Failed to fetch item detail';
    return { success: false, data: null, error: errorMessage };
  }
};

/**
 * Service response type for getStorePricebookMetadata - returns PricebookMetadataModel
 */
export interface PricebookMetadataServiceResponse {
  success: boolean;
  data: PricebookMetadataModel | null;
  error: any;
}

/**
 * Get store pricebook metadata (departments, taxes, promotions)
 * 
 * @param storeId - The ID of the store
 * @returns Promise<PricebookMetadataServiceResponse> with PricebookMetadataModel
 */
export const getStorePricebookMetadata = async (
  storeId: string
): Promise<PricebookMetadataServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_STORE_METADATA(storeId),
      {}
    ) as AxiosResponse;

    if (response?.data) {
      const model = PricebookMetadataModel.fromServerData(response.data);
      return {
        success: true,
        data: model,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch pricebook metadata'
    };
  }
};

/**
 * Create a new pricebook item
 * 
 * @param storeId - The ID of the store
 * @param itemData - The item data to create
 * @returns Promise<CreatePricebookItemResponse>
 */
export const createPricebookItem = async (
  storeId: string,
  itemData: CreatePricebookItemRequest
): Promise<CreatePricebookItemResponse> => {
  const response = await ThrottledApi.post(
    PRICEBOOK_ENDPOINTS.CREATE_STORE_ITEM(storeId),
    itemData
  ) as AxiosResponse;

  if (response?.data) {
    return {
      success: true,
      data: response.data,
      error: null
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received'
  };
};

/**
 * Update an existing pricebook item
 * 
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item to update
 * @param itemData - The item data to update
 * @returns Promise<UpdatePricebookItemResponse>
 */
export const updatePricebookItem = async (
  storeId: string,
  scanCode: string,
  itemData: UpdatePricebookItemRequest
): Promise<UpdatePricebookItemResponse> => {
  const response = await ThrottledApi.put(
    PRICEBOOK_ENDPOINTS.UPDATE_STORE_ITEM(storeId, scanCode),
    itemData
  ) as AxiosResponse;

  if (response?.data) {
    return {
      success: true,
      data: response.data,
      error: null
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received'
  };
};

/**
 * Get inventory settings for a specific item
 *
 * @param storeId - The ID of the store
 * @param scanCode - The scan/pos code of the item
 * @returns Promise<ItemInventorySettingsServiceResponse>
 */
export const getItemInventorySettings = async (
  storeId: string,
  scanCode: string
): Promise<ItemInventorySettingsServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_ITEM_INVENTORY_SETTINGS(storeId, scanCode),
      {}
    ) as AxiosResponse;

    if (response?.data) {
      const model = ItemInventorySettingsModel.fromServerData(response.data);
      return {
        success: true,
        data: model,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received',
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch item inventory settings',
    };
  }
};

/**
 * Get inventory movements for a specific item and date range
 *
 * @param storeId - The ID of the store
 * @param scanCode - The scan/pos code of the item
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Promise<ItemInventoryMovementsServiceResponse>
 */
export const getItemInventoryMovements = async (
  storeId: string,
  scanCode: string,
  startDate: Date | null,
  endDate: Date | null
): Promise<ItemInventoryMovementsServiceResponse> => {
  try {
    const formatDate = (date: Date | null) => {
      if (!date) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear());
      return `${year}-${month}-${day}`;
    };

    let url = PRICEBOOK_ENDPOINTS.GET_ITEM_INVENTORY_MOVEMENTS(storeId, scanCode);
    const queryParams: string[] = [];
    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    if (startDateStr) {
      queryParams.push(`start_date=${encodeURIComponent(startDateStr)}`);
    }
    if (endDateStr) {
      queryParams.push(`end_date=${encodeURIComponent(endDateStr)}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    const response = await ThrottledApi.get(url, {}) as AxiosResponse;

    if (response?.data) {
      const model = ItemInventoryMovementsModel.fromServerData(response.data);
      return {
        success: true,
        data: model,
        error: null,
      };
    }

    return {
      success: true,
      data: ItemInventoryMovementsModel.createEmpty(),
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch item inventory movements',
    };
  }
};

/**
 * Get finalized purchase lines for an item and date range
 *
 * @param storeId - The ID of the current store
 * @param storeIds - The IDs of the stores to include in the request
 * @param scanCode - The item POS code
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Promise<ItemPurchasePricingServiceResponse>
 */
export const getItemPurchasePricing = async (
  storeId: string,
  storeIds: string[],
  scanCode: string,
  startDate: Date | null,
  endDate: Date | null
): Promise<ItemPurchasePricingServiceResponse> => {
  try {
    const formatDate = (date: Date | null) => {
      if (!date) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear());
      return `${year}-${month}-${day}`;
    };

    let url = PRICEBOOK_ENDPOINTS.GET_FINALIZED_PURCHASE_LINES(storeId);
    const queryParams: string[] = [];

    if (scanCode) {
      queryParams.push(`item_pos_code=${encodeURIComponent(scanCode)}`);
    }

    const resolvedStoreIds = Array.from(
      new Set(
        [storeId, ...storeIds]
          .map((value) => String(value || '').trim())
          .filter((value) => Boolean(value))
      )
    );

    resolvedStoreIds.forEach((resolvedStoreId) => {
      queryParams.push(`store_ids=${encodeURIComponent(resolvedStoreId)}`);
    });

    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    if (startDateStr) {
      queryParams.push(`start_date=${encodeURIComponent(startDateStr)}`);
    }
    if (endDateStr) {
      queryParams.push(`end_date=${encodeURIComponent(endDateStr)}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    const response = await ThrottledApi.get(url, {}) as AxiosResponse;
    const responseData = response?.data;

    if (responseData && typeof responseData === 'object') {
      return {
        success: true,
        data: ItemPurchasePricingModel.fromServerData(responseData),
        error: null,
      };
    }

    return {
      success: true,
      data: ItemPurchasePricingModel.createEmpty(),
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch purchase pricing',
    };
  }
};

/**
 * Update inventory settings for an item
 *
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item
 * @param requestData - Inventory settings payload
 * @returns Promise<UpdateInventorySettingsResponse>
 */
export const updateItemInventorySettings = async (
  storeId: string,
  scanCode: string,
  requestData: UpdateInventorySettingsRequest
): Promise<UpdateInventorySettingsResponse> => {
  const response = await ThrottledApi.put(
    PRICEBOOK_ENDPOINTS.UPDATE_ITEM_INVENTORY_SETTINGS(storeId, scanCode),
    requestData
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
    return {
      success: true,
      data: response.data || null,
      error: null,
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received',
  };
};

/**
 * Update opening balance for an item inventory
 *
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item
 * @param requestData - Opening balance payload
 * @returns Promise<UpdateItemOpeningBalanceResponse>
 */
export const updateItemOpeningBalance = async (
  storeId: string,
  scanCode: string,
  requestData: UpdateItemOpeningBalanceRequest
): Promise<UpdateItemOpeningBalanceResponse> => {
  const response = await ThrottledApi.put(
    PRICEBOOK_ENDPOINTS.UPDATE_ITEM_OPENING_BALANCE(storeId, scanCode),
    requestData
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
    return {
      success: true,
      data: response.data || null,
      error: null,
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received',
  };
};

/**
 * Create inventory adjustment for an item
 *
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item
 * @param requestData - Adjustment payload
 * @returns Promise<CreateItemInventoryAdjustmentResponse>
 */
export const createItemInventoryAdjustment = async (
  storeId: string,
  scanCode: string,
  requestData: CreateItemInventoryAdjustmentRequest
): Promise<CreateItemInventoryAdjustmentResponse> => {
  const response = await ThrottledApi.post(
    PRICEBOOK_ENDPOINTS.CREATE_ITEM_INVENTORY_ADJUSTMENT(storeId, scanCode),
    requestData
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
    return {
      success: true,
      data: response.data || null,
      error: null,
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received',
  };
};

/**
 * Create a manual finalized purchase line
 *
 * @param storeId - The ID of the store
 * @param requestData - Manual purchase line payload
 * @returns Promise<CreateManualPurchaseLineResponse>
 */
export const createManualPurchaseLine = async (
  storeId: string,
  requestData: CreateManualPurchaseLineRequest
): Promise<CreateManualPurchaseLineResponse> => {
  const response = await ThrottledApi.post(
    PRICEBOOK_ENDPOINTS.CREATE_MANUAL_PURCHASE_LINE(storeId),
    requestData
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
    return {
      success: true,
      data: response.data || null,
      error: null,
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received',
  };
};

/**
 * Get finalized purchase vendors for a store
 *
 * @param storeId - The ID of the store
 * @returns Promise<FinalizedPurchaseVendorsServiceResponse> with PurchaseVendorsListModel
 */
export const getFinalizedPurchaseVendors = async (
  storeId: string
): Promise<FinalizedPurchaseVendorsServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_FINALIZED_PURCHASE_VENDORS(storeId),
      {}
    ) as AxiosResponse;

    const responseData = response?.data;

    if (Array.isArray(responseData)) {
      return {
        success: true,
        data: PurchaseVendorsListModel.fromServerData(responseData),
        error: null,
      };
    }

    return {
      success: true,
      data: PurchaseVendorsListModel.createEmpty(),
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch finalized purchase vendors',
    };
  }
};

export const createStoreVendor = async (
  storeId: string,
  requestData: CreateStoreVendorRequest
): Promise<CreateStoreVendorResponse> => {
  try {
    const response = await ThrottledApi.post(
      PRICEBOOK_ENDPOINTS.CREATE_STORE_VENDOR(storeId),
      requestData
    ) as AxiosResponse;

    if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
      return {
        success: true,
        data: response.data || null,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received',
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to create vendor',
    };
  }
};

/**
 * Create a new linked code
 *
 * @param requestData - The linked code payload
 * @returns Promise<CreateLinkedCodeResponse>
 */
export const createLinkedCode = async (
  requestData: CreateLinkedCodeRequest
): Promise<CreateLinkedCodeResponse> => {
  try {
    const response = await ThrottledApi.post(
      PRICEBOOK_ENDPOINTS.CREATE_LINKED_CODE,
      requestData
    ) as AxiosResponse;

    if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
      return {
        success: true,
        data: response.data || null,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received',
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to create linked code',
    };
  }
};

/**
 * Get linked codes for a store
 *
 * @param storeId - The ID of the store
 * @returns Promise<LinkedCodesServiceResponse>
 */
export const getLinkedCodes = async (
  storeId: string,
  filters?: {
    search?: string;
    status?: string;
  }
): Promise<LinkedCodesServiceResponse> => {
  try {
    let url = PRICEBOOK_ENDPOINTS.GET_LINKED_CODES(storeId);
    const queryParams: string[] = [];

    if (filters?.search && filters.search.trim() !== '') {
      queryParams.push(`search=${encodeURIComponent(filters.search.trim())}`);
    }

    if (filters?.status && filters.status !== 'all') {
      queryParams.push(`status=${encodeURIComponent(filters.status)}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    const response = await ThrottledApi.get(
      url,
      {}
    ) as AxiosResponse;

    const responseData = response?.data;

    if (responseData && Array.isArray(responseData.items)) {
      return {
        success: true,
        data: LinkedCodeListModel.fromServerData(responseData),
        error: null,
      };
    }

    return {
      success: true,
      data: LinkedCodeListModel.createEmpty(),
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch linked codes',
    };
  }
};

/**
 * Get all price groups
 *
 * @param storeId - The ID of the store
 * @param search - Optional search string
 * @returns Promise<PriceGroupsServiceResponse> with price group list payload
 */
export const getPriceGroups = async (
  storeId: string,
  search?: string
): Promise<PriceGroupsServiceResponse> => {
  try {
    let url = PRICEBOOK_ENDPOINTS.GET_PRICE_GROUPS;
    const queryParams: string[] = [];

    if (storeId) {
      queryParams.push(`store_id=${encodeURIComponent(storeId)}`);
    }

    if (search && search.trim() !== '') {
      queryParams.push(`search=${encodeURIComponent(search.trim())}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    const response = await ThrottledApi.get(url, {}) as AxiosResponse;
    const responseData = response?.data;

    if (responseData?.detail) {
      return {
        success: false,
        data: null,
        error: responseData.detail,
      };
    }

    if (responseData && typeof responseData === 'object') {
      return {
        success: true,
        data: PriceGroupsListModel.fromServerData(responseData),
        error: null,
      };
    }

    return {
      success: true,
      data: PriceGroupsListModel.createEmpty(),
      error: null,
    };
  } catch (error: any) {
    const errorData = error.response?.data;
    const errorMessage = errorData?.detail || errorData || error.message || 'Failed to fetch price groups';

    return {
      success: false,
      data: null,
      error: errorMessage,
    };
  }
};

/**
 * Get a price group detail by ID
 *
 * @param priceGroupId - The price group ID
 * @returns Promise<PriceGroupDetailServiceResponse>
 */
export const getPriceGroupDetail = async (
  priceGroupId: string
): Promise<PriceGroupDetailServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_PRICE_GROUP_DETAIL(priceGroupId),
      {}
    ) as AxiosResponse;

    const responseData = response?.data;

    if (responseData?.detail) {
      return {
        success: false,
        data: null,
        error: responseData.detail,
      };
    }

    if (responseData && typeof responseData === 'object') {
      return {
        success: true,
        data: PriceGroupDetailModel.fromServerData(responseData),
        error: null,
      };
    }

    return {
      success: true,
      data: PriceGroupDetailModel.fromServerData(null),
      error: null,
    };
  } catch (error: any) {
    const errorData = error.response?.data;
    const errorMessage = errorData?.detail || errorData || error.message || 'Failed to fetch price group detail';

    return {
      success: false,
      data: null,
      error: errorMessage,
    };
  }
};

/**
 * Create a price group
 *
 * @param requestData - The price group payload
 * @returns Promise<PriceGroupMutationResponse>
 */
export const createPriceGroup = async (
  requestData: {
    store_id: string;
    name: string;
    value: number;
    is_active: boolean;
  }
): Promise<{ success: boolean; data: any; error: any }> => {
  try {
    const response = await ThrottledApi.post(
      PRICEBOOK_ENDPOINTS.CREATE_PRICE_GROUP,
      requestData
    ) as AxiosResponse;

    if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
      return {
        success: true,
        data: response.data || null,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received',
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to create price group',
    };
  }
};

/**
 * Update a price group
 *
 * @param priceGroupId - The price group ID
 * @param requestData - The price group payload
 * @returns Promise<PriceGroupMutationResponse>
 */
export const updatePriceGroup = async (
  priceGroupId: string,
  requestData: {
    name: string;
    value: number;
    is_active: boolean;
  }
): Promise<{ success: boolean; data: any; error: any }> => {
  try {
    const response = await ThrottledApi.put(
      PRICEBOOK_ENDPOINTS.UPDATE_PRICE_GROUP(priceGroupId),
      requestData
    ) as AxiosResponse;

    if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
      return {
        success: true,
        data: response.data || null,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received',
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to update price group',
    };
  }
};

/**
 * Assign items to a price group
 *
 * @param priceGroupId - The price group ID
 * @param requestData - The payload containing selected item scan codes
 * @returns Promise<AssignPriceGroupItemsServiceResponse>
 */
export const assignPriceGroupItems = async (
  priceGroupId: string,
  requestData: AssignPriceGroupItemsRequest
): Promise<AssignPriceGroupItemsServiceResponse> => {
  try {
    const response = await ThrottledApi.post(
      PRICEBOOK_ENDPOINTS.ASSIGN_PRICE_GROUP_ITEMS(priceGroupId),
      requestData
    ) as AxiosResponse;

    if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
      return {
        success: true,
        data: response.data || null,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received',
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to assign items to price group',
    };
  }
};

/**
 * Update an existing linked code
 *
 * @param storeId - The store ID
 * @param linkedCodeId - The linked code ID
 * @param requestData - The linked code payload
 * @returns Promise<UpdateLinkedCodeResponse>
 */
export const updateLinkedCode = async (
  storeId: string,
  scanCode: string,
  requestData: UpdateLinkedCodeRequest
): Promise<UpdateLinkedCodeResponse> => {
  try {
    const response = await ThrottledApi.put(
      PRICEBOOK_ENDPOINTS.UPDATE_LINKED_CODE(storeId, scanCode),
      requestData
    ) as AxiosResponse;

    if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
      return {
        success: true,
        data: response.data || null,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received',
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to update linked code',
    };
  }
};

/**
 * Service response type for getItemPriceComparison - returns PriceComparisonModel[]
 */
export interface ItemPriceComparisonServiceResponse {
  success: boolean;
  data: PriceComparisonModel[] | null;
  error: any;
}

/**
 * Get item price comparison across stores
 *
 * @param scanCode - The scan code of the item
 * @returns Promise<ItemPriceComparisonServiceResponse> with PriceComparisonModel[]
 */
export const getItemPriceComparison = async (
  scanCode: string
): Promise<ItemPriceComparisonServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_ITEM_PRICE_COMPARISON(scanCode),
      {}
    ) as AxiosResponse;

    const responseData = response?.data;

    if (Array.isArray(responseData)) {
      // Create model instances from API response (same pattern as getStorePricebookItems)
      const models = responseData.map((item: any) => PriceComparisonModel.fromServerData(item));
      return {
        success: true,
        data: models,
        error: null,
      };
    }

    return {
      success: true,
      data: [],
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch item price comparison',
    };
  }
};

/**
 * Delete a pricebook item
 * 
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item to delete
 * @returns Promise<DeletePricebookItemResponse>
 */
export const deletePricebookItem = async (
  storeId: string,
  scanCode: string
): Promise<DeletePricebookItemResponse> => {
  const response = await ThrottledApi.delete(
    PRICEBOOK_ENDPOINTS.DELETE_STORE_ITEM(storeId, scanCode),
    {}
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 204) {
    return {
      success: true,
      data: response.data || null,
      error: null
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received'
  };
};

/**
 * Manually expire transition-phase queue rows for an item and revert to base pricebook data.
 *
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item
 * @returns Promise<ExpireTransitionPricebookItemResponse>
 */
export const expireTransitionPricebookItem = async (
  storeId: string,
  scanCode: string,
  storeIds?: string[]
): Promise<ExpireTransitionPricebookItemResponse> => {
  const response = await ThrottledApi.post(
    PRICEBOOK_ENDPOINTS.EXPIRE_TRANSITION_ITEM(storeId, scanCode),
    storeIds && storeIds.length > 0 ? { store_ids: storeIds } : {}
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 204) {
    return {
      success: true,
      data: response.data || null,
      message: response.data?.message || null,
      error: null
    };
  }

  return {
    success: false,
    data: null,
    message: null,
    error: 'No data received'
  };
};

/**
 * Get inventory summary for a store
 * 
 * @param storeId - The ID of the store
 * @returns Promise<InventorySummaryServiceResponse> with InventorySummaryModel
 */
export const getInventorySummary = async (
  storeId: string
): Promise<InventorySummaryServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_INVENTORY_SUMMARY(storeId),
      {}
    ) as AxiosResponse;

    if (response?.data) {
      // Create model from API response (same pattern as getStorePricebookItems)
      const model = InventorySummaryModel.fromServerData(response.data);
      return {
        success: true,
        data: model,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch inventory summary'
    };
  }
};

/**
 * Service response type for getItemSalesSummary - returns SalesSummaryModel
 */
export interface ItemSalesSummaryServiceResponse {
  success: boolean;
  data: SalesSummaryModel | null;
  error: any;
}

export interface ItemMonthlySalesSummaryServiceResponse {
  success: boolean;
  data: MonthlySalesSummaryModel | null;
  error: any;
}

/**
 * Get item sales summary
 * 
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item
 * @param startDate - Optional start date for the sales summary (YYYY-MM-DD format)
 * @param endDate - Optional end date for the sales summary (YYYY-MM-DD format)
 * @returns Promise<ItemSalesSummaryServiceResponse> with SalesSummaryModel
 */
export const getItemSalesSummary = async (
  storeId: string,
  scanCode: string,
  startDate?: string | null,
  endDate?: string | null
): Promise<ItemSalesSummaryServiceResponse> => {
  try {
    let url = PRICEBOOK_ENDPOINTS.GET_ITEM_SALES_SUMMARY(storeId, scanCode);
    
    // Build query parameters
    const queryParams: string[] = [];
    
    if (startDate) {
      queryParams.push(`start_date=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      queryParams.push(`end_date=${encodeURIComponent(endDate)}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await ThrottledApi.get(url, {}) as AxiosResponse;

    if (response?.data) {
      // Create model instance from API response (same pattern as getStorePricebookItems)
      const model = SalesSummaryModel.fromServerData(response.data);
      return {
        success: true,
        data: model,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch item sales summary'
    };
  }
};

export const getBulkUpdateMultiStoreItems = async (
  storeId: string,
  filters: BulkMultiStoreItemsFilters,
  page: number = 1,
  pageSize: number = 20
): Promise<BulkMultiStoreItemsServiceResponse> => {
  try {
    let url = PRICEBOOK_ENDPOINTS.GET_MULTI_STORE_ITEMS(storeId);
    const queryParams = new URLSearchParams();

    queryParams.append('page', String(page));
    queryParams.append('page_size', String(pageSize));

    (filters.store_ids || []).forEach((storeIdValue) => {
      if (storeIdValue) {
        queryParams.append('store_ids', storeIdValue);
      }
    });

    if (filters.search) {
      queryParams.append('search', filters.search);
    }
    if (filters.dept_code) {
      queryParams.append('dept_code', filters.dept_code);
    }
    if (filters.tax_code) {
      queryParams.append('tax_code', filters.tax_code);
    }
    if (filters.vendor_id) {
      queryParams.append('vendor_id', filters.vendor_id);
    }
    if (filters.pricegroup) {
      queryParams.append('pricegroup', filters.pricegroup);
    }
    if (filters.age_restriction) {
      queryParams.append('age_restriction', filters.age_restriction);
    }
    if (filters.linked_code) {
      queryParams.append('linked_code', filters.linked_code);
    }
    if (filters.min_price) {
      queryParams.append('min_price', filters.min_price);
    }
    if (filters.max_price) {
      queryParams.append('max_price', filters.max_price);
    }
    if (filters.food_stampable !== undefined) {
      queryParams.append('food_stampable', String(filters.food_stampable));
    }
    if (filters.min_margin) {
      queryParams.append('min_margin', filters.min_margin);
    }
    if (filters.max_margin) {
      queryParams.append('max_margin', filters.max_margin);
    }

    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await ThrottledApi.get(url, {}) as AxiosResponse;
    const responseData = response?.data;

    if (responseData?.detail) {
      return {
        success: false,
        data: null,
        error: responseData.detail,
      };
    }

    if (responseData && responseData.items) {
      return {
        success: true,
        data: BulkUpdateItemsListModel.fromServerData(responseData as BulkUpdateItemsApiResponse),
        error: null,
      };
    }

    return {
      success: true,
      data: BulkUpdateItemsListModel.createEmpty(),
      error: null,
    };
  } catch (error: any) {
    const errorData = error.response?.data;
    const errorMessage = errorData?.detail || errorData || error.message || 'Failed to fetch bulk update items';

    return {
      success: false,
      data: null,
      error: errorMessage,
    };
  }
};

export const getItemMonthlySalesSummary = async (
  storeId: string,
  scanCode: string
): Promise<ItemMonthlySalesSummaryServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_ITEM_SALES_SUMMARY_MONTHLY(storeId, scanCode),
      {}
    ) as AxiosResponse;

    if (response?.data) {
      return {
        success: true,
        data: MonthlySalesSummaryModel.fromServerData(response.data),
        error: null,
      };
    }

    return {
      success: true,
      data: MonthlySalesSummaryModel.createEmpty(),
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch item monthly sales summary',
    };
  }
};

/**
 * Send items to POS
 * 
 * @param storeId - The ID of the store
 * @param requestData - The request data containing scan_code, target_store_ids and optional scheduled_date/scheduled_time
 * @returns Promise<SendItemsToPosResponse>
 */
export const sendItemsToPos = async (
  storeId: string,
  requestData: SendItemsToPosRequest
): Promise<SendItemsToPosResponse> => {
  // Build request body - use grade_id for gas prices, scan_code for items
  const requestBody: any = {
    target_store_ids: requestData.target_store_ids,
  };
  
  // Include scan_code if provided (for items)
  if (requestData.scan_code) {
    requestBody.scan_code = requestData.scan_code;
  }
  
  // Include grade_id if provided (for gas prices)
  if (requestData.grade_id) {
    requestBody.grade_id = requestData.grade_id;
  }
  
  // Only include scheduled_date if it's provided
  if (requestData.scheduled_date) {
    requestBody.scheduled_date = requestData.scheduled_date;
  }
  
  // Only include scheduled_time if it's provided
  if (requestData.scheduled_time) {
    requestBody.scheduled_time = requestData.scheduled_time;
  }

  const response = await ThrottledApi.post(
    PRICEBOOK_ENDPOINTS.SEND_ITEMS_TO_POS(storeId),
    requestBody
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
    // Handle response structure: { message: "...", items: [...] }
    return {
      success: true,
      data: response.data || null,
      error: null
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received'
  };
};

/**
 * Service response type for getGasPriceGrades - returns GasPriceModel[]
 */
export interface GasPriceGradesServiceResponse {
  success: boolean;
  data: GasPriceModel[] | null;
  error: any;
}

/**
 * Get gas price grades for a store
 * 
 * @param storeId - The ID of the store
 * @returns Promise<GasPriceGradesServiceResponse> with GasPriceModel[]
 */
export const getGasPriceGrades = async (
  storeId: string
): Promise<GasPriceGradesServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_GAS_PRICE_GRADES(storeId),
      {}
    ) as AxiosResponse;

    if (response?.data && Array.isArray(response.data)) {
      // Create model instances from API response (same pattern as getStorePricebookItems)
      const models = response.data.map((item: any) => GasPriceModel.fromServerData(item));
      return {
        success: true,
        data: models,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch gas price grades'
    };
  }
};

/**
 * Bulk update pricebook items
 * 
 * @param storeId - The ID of the store (must match store_id in request body)
 * @param requestData - The request data containing the anchor store_id and item-level store targets
 * @returns Promise<BulkUpdatePricebookItemsResponse>
 */
export const bulkUpdatePricebookItems = async (
  storeId: string,
  requestData: BulkUpdatePricebookItemsRequest
): Promise<BulkUpdatePricebookItemsResponse> => {
  try {
    const response = await ThrottledApi.put(
      PRICEBOOK_ENDPOINTS.BULK_UPDATE_ITEMS(storeId),
      requestData
    ) as AxiosResponse;

    if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
      return {
        success: true,
        data: response.data || null,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    // Handle error response with detail key (e.g., "Store ID in request body must match the store ID in the URL path")
    let errorMessage = 'Failed to bulk update items';
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (typeof error.response?.data === 'string') {
      errorMessage = error.response.data;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * Update gas price grade
 * 
 * @param storeId - The ID of the store
 * @param gradeId - The ID of the grade (e.g., "020", "096")
 * @param requestData - The request data containing store_id, grade_id, and new_price
 * @returns Promise<UpdateGasPriceResponse>
 */
export const updateGasPriceGrade = async (
  storeId: string,
  gradeId: string,
  requestData: UpdateGasPriceRequest
): Promise<UpdateGasPriceResponse> => {
  const requestBody = {
    store_id: requestData.store_id,
    grade_id: requestData.grade_id,
    new_price: requestData.new_price,
  };

  const response = await ThrottledApi.put(
    PRICEBOOK_ENDPOINTS.UPDATE_GAS_PRICE_GRADE(storeId, gradeId),
    requestBody
  ) as AxiosResponse;

  if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
    return {
      success: true,
      data: response.data || null,
      error: null
    };
  }

  return {
    success: false,
    data: null,
    error: 'No data received'
  };
};

/**
 * Get item history for a specific store and item
 * 
 * @param storeId - The ID of the store
 * @param scanCode - The scan code of the item
 * @returns Promise<ItemHistoryResponse> with array of history items
 */
export const getItemHistory = async (
  storeId: string,
  scanCode: string
): Promise<ItemHistoryResponse> => {
  try {
    const response = await ThrottledApi.get(
      PRICEBOOK_ENDPOINTS.GET_ITEM_HISTORY(storeId, scanCode),
      {}
    ) as AxiosResponse;

    const responseData = response?.data;

    // Handle error response with detail key
    if (responseData?.detail) {
      return {
        success: false,
        data: null,
        error: responseData.detail,
      };
    }

    // Handle success response: array of history items
    if (Array.isArray(responseData)) {
      return {
        success: true,
        data: responseData,
        error: null,
      };
    }

    // Handle empty or unexpected response
    return {
      success: true,
      data: [],
      error: null,
    };
  } catch (error: any) {
    // Handle error response with detail key
    const errorData = error.response?.data;
    const errorMessage = errorData?.detail || errorData || error.message || 'Failed to fetch item history';
    
    return {
      success: false,
      data: null,
      error: errorMessage,
    };
  }
};

export const pricebookService = {
  getStorePricebookItems,
  getBulkUpdateMultiStoreItems,
  getStorePricebookItemDetail,
  getStorePricebookMetadata,
  createPricebookItem,
  updatePricebookItem,
  getItemInventorySettings,
  getItemInventoryMovements,
  updateItemInventorySettings,
  updateItemOpeningBalance,
  createItemInventoryAdjustment,
  createManualPurchaseLine,
  getItemPurchasePricing,
  getFinalizedPurchaseVendors,
  createLinkedCode,
  updateLinkedCode,
  deletePricebookItem,
  expireTransitionPricebookItem,
  sendItemsToPos,
  bulkUpdatePricebookItems,
  getInventorySummary,
  getItemSalesSummary,
  getItemMonthlySalesSummary,
  getItemHistory,
  getGasPriceGrades,
  updateGasPriceGrade,
  getPriceGroups,
  getPriceGroupDetail,
  createPriceGroup,
  updatePriceGroup,
  assignPriceGroupItems,
};
